export type Compound = 'SOFT' | 'MEDIUM' | 'HARD' | 'INTERMEDIATE' | 'WET' | 'UNKNOWN';
export type TrackStatus = 'GREEN' | 'YELLOW' | 'RED' | 'SC' | 'VSC' | 'FINISHED' | 'UNKNOWN';
export type DriverStatus = 'RUNNING' | 'PIT' | 'OUT' | 'RETIRED';
export type DrsStatus = 'OFF' | 'AVAILABLE' | 'ON' | 'UNKNOWN';
export type LivePhase = 'unconfigured' | 'connecting' | 'waiting' | 'streaming' | 'recovering' | 'stale' | 'error' | 'ended';

/** Capabilities are declared by the adapter and consumed by view projections.
 * They are deliberately source-agnostic so components never branch on a provider name. */
export interface SessionCapabilities {
  miniSectors: 'available' | 'unsupported' | 'unknown';
  sectorTimes: 'available' | 'unsupported' | 'unknown';
  carTelemetry: 'available' | 'unsupported' | 'unknown';
  location: 'available' | 'unsupported' | 'unknown';
}

export function capabilitiesForSource(source: SessionSummary['source']): SessionCapabilities {
  if (source === 'livelite') return { miniSectors: 'available', sectorTimes: 'available', carTelemetry: 'unknown', location: 'unknown' };
  if (source === 'openf1') return { miniSectors: 'unsupported', sectorTimes: 'available', carTelemetry: 'available', location: 'available' };
  return { miniSectors: 'unsupported', sectorTimes: 'unknown', carTelemetry: 'unknown', location: 'unknown' };
}

/**
 * Resolve capabilities from the canonical facts that will actually be shown.
 * Source defaults remain a compatibility fallback for an empty recording, but
 * a provider may add a field that the other provider does not expose. This is
 * what lets a fused Lite + OpenF1 recording advertise the union without
 * leaking provider names into view code.
 */
export function capabilitiesForRecording(recording: Pick<RaceRecording, 'session' | 'events' | 'track' | 'capabilities'>): SessionCapabilities {
  const fallback = recording.capabilities ?? capabilitiesForSource(recording.session.source);
  const hasTiming = recording.events.some(event => event.type === 'timing');
  const hasMiniSectors = recording.events.some(event => event.type === 'timing'
    && (event.patch.miniSectors !== undefined || event.patch.sectorDetails !== undefined));
  const hasSectorTimes = recording.events.some(event => event.type === 'timing'
    && (event.patch.sectors?.some(value => value !== null) || event.patch.recentLaps?.some(lap => lap.sectors.some(value => value !== null))));
  const hasCarTelemetry = recording.events.some(event => event.type === 'telemetry'
    && (event.patch.speed !== undefined || event.patch.rpm !== undefined || event.patch.gear !== undefined
      || event.patch.throttle !== undefined || event.patch.brake !== undefined || event.patch.drs !== undefined));
  const hasLocation = recording.track.length > 2 || recording.events.some(event => event.type === 'telemetry'
    && (event.patch.x !== undefined || event.patch.y !== undefined));
  const available = (detected: boolean, declared: SessionCapabilities[keyof SessionCapabilities]) => detected ? 'available' as const : declared;
  return {
    miniSectors: available(hasMiniSectors, fallback.miniSectors),
    sectorTimes: available(hasSectorTimes || hasTiming && fallback.sectorTimes === 'available', fallback.sectorTimes),
    carTelemetry: available(hasCarTelemetry, fallback.carTelemetry),
    location: available(hasLocation, fallback.location),
  };
}

export interface SessionSummary {
  key: string;
  title: string;
  country: string;
  circuit: string;
  sessionName: string;
  dateStart: string;
  dateEnd: string;
  totalLaps: number | null;
  source: 'demo' | 'openf1' | 'livelite' | 'jolpica';
}

/** Complete session metadata; recording chunks retain their own relative clocks. */
export interface ReplayManifest {
  session: SessionSummary;
  durationMs: number;
  chunkDurationMs: 600000;
}

export interface DriverInfo {
  number: number;
  code: string;
  name: string;
  team: string;
  teamColor: string;
}
export interface Stint { compound: Compound; startLap: number; endLap: number | null; tyreAgeAtStart: number | null; startLapKnown?: boolean }
export type MiniSectorState = 'unknown' | 'pending' | 'complete' | 'personal-best' | 'session-best';
/** Attribution is unknown for values inherited from an initial snapshot. */
export interface TimingSampleMeta { lap: number | null; updatedAt: string | null }
export interface SectorTiming extends TimingSampleMeta { state: MiniSectorState; segments: TimingSampleMeta[] }
export type SpeedTrap = 'I1' | 'I2' | 'FL' | 'ST';
/** Discrete official timing point, never continuous car telemetry. */
export interface SpeedTrapSample { value: number; lap: number | null; atMs: number | null }
export interface SpeedTrapTiming { latest: SpeedTrapSample | null; best: SpeedTrapSample | null }
export interface CompletedLap { lap: number; atMs: number; duration: number | null; sectors: [number | null, number | null, number | null]; provenance: 'direct' | 'merged' | 'derived'; partial?: boolean; deleted?: boolean }
export interface DataCoverage { startMs: number; endMs: number; missingStart: boolean; gaps: { fromMs: number; toMs: number; reason: string }[]; note?: string }
export interface DriverTiming {
  position: number | null;
  /** Verified upstream starting grid position; never inferred from a mid-session snapshot. */
  startingPosition?: number | null;
  lap: number;
  gapToLeader: number | string | null;
  interval: number | string | null;
  /** Original source UTC time; independent of replay window and rolling seeds. */
  intervalUpdatedAt?: string | null;
  /** Stream health is independent of a field changing; Lite sends differences. */
  timingUpdatedAt?: string | null;
  completedLaps?: number;
  miniSectors?: MiniSectorState[][];
  sectorDetails?: SectorTiming[];
  recentLaps?: CompletedLap[];
  lastLap: number | null;
  bestLap: number | null;
  bestLapNumber?: number | null;
  /** Observation time of an authoritative record change; null on an initial snapshot. */
  bestLapAtMs?: number | null;
  speedTraps?: Partial<Record<SpeedTrap, SpeedTrapTiming>>;
  sectors: [number | null, number | null, number | null];
  compound: Compound;
  tyreAge: number | null;
  pitCount: number;
  status: DriverStatus;
  stints: Stint[];
}
export interface Telemetry {
  speed: number | null;
  rpm: number | null;
  gear: number | null;
  throttle: number | null;
  brake: boolean | null;
  drs?: DrsStatus;
  x: number | null;
  y: number | null;
}
export interface DriverState extends DriverInfo, DriverTiming {
  telemetry: Telemetry;
  telemetryAtMs: number | null;
  locationAtMs: number | null;
  speedHistory: { atMs: number; speed: number }[];
}
export interface TrackPoint { x: number; y: number }
export interface RaceMessage {
  id: string;
  atMs: number;
  category: 'FLAG' | 'CONTROL' | 'PIT' | 'FASTEST' | 'INFO';
  text: string;
  originalText?: string;
  flag?: TrackStatus;
  driverNumber?: number;
}
export interface PitStop {
  id: string;
  atMs: number;
  driverNumber: number;
  lap: number;
  stopDuration: number | null;
  laneDuration: number | null;
}
export interface Weather {
  /** Original source UTC time; missing in older recordings. */
  updatedAt?: string | null;
  airTemperature: number | null;
  trackTemperature: number | null;
  humidity: number | null;
  windSpeed: number | null;
  rainfall: boolean | null;
}
/**
 * Provenance attached by an ingestion adapter.  It is optional so historical
 * recordings and synthetic fixtures remain backwards compatible, but a live
 * adapter should populate it for every normalized event.  The raw journal is
 * still the authoritative payload; this is the correlation point used when a
 * normalized fact is replayed or re-parsed.
 */
export interface EventProvenance {
  channel: string;
  rawId: string;
  sourceAt: string | null;
  receivedAt: string;
  snapshot: boolean;
}
interface EventBase { id: string; atMs: number; provenance?: EventProvenance }
export type RaceEvent =
  | (EventBase & { type: 'timing'; driverNumber: number; patch: Partial<DriverTiming> })
  | (EventBase & { type: 'telemetry'; driverNumber: number; patch: Partial<Telemetry> })
  | (EventBase & { type: 'message'; message: RaceMessage })
  | (EventBase & { type: 'pit'; stop: PitStop })
  | (EventBase & { type: 'weather'; weather: Weather })
  | (EventBase & { type: 'status'; status: TrackStatus })
  | (EventBase & { type: 'session'; patch: Pick<SessionSummary, 'totalLaps'> })
  | (EventBase & { type: 'phase'; phase: 1 | 2 | 3 | null });

export interface RaceRecording {
  schemaVersion: 1;
  session: SessionSummary;
  drivers: DriverInfo[];
  track: TrackPoint[];
  events: RaceEvent[];
  durationMs: number;
  notice: string | null;
  coverage?: DataCoverage;
  capabilities?: SessionCapabilities;
}
export interface RaceSnapshot {
  atMs: number;
  session: SessionSummary;
  drivers: DriverState[];
  messages: RaceMessage[];
  pitStops: PitStop[];
  weather: Weather;
  trackStatus: TrackStatus;
  qualifyingPhase?: 1 | 2 | 3 | null;
  lap: number;
  track: TrackPoint[];
  coverage?: DataCoverage;
  capabilities?: SessionCapabilities;
}
export interface SourceStatus {
  phase?: LivePhase;
  configured: boolean;
  connected: boolean;
  lastEventAt: string | null;
  message: string;
  sessionKey?: string;
  recovering?: boolean;
  gap?: { from: string; to: string; recovered: boolean } | null;
}
export interface ApiError { error: { code: string; message: string } }
